import { useState, useEffect } from "react";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import MailIcon from "@mui/icons-material/Mail";
import {
  fetchSizeOptions,
  getBulkLocationQuery,
  fetchPoOrMaterial,
} from "../../../label_print/src/model/PrintingModel";
import { getStickerPdf, getWarehousePdf, updateBinBulk } from "../model/GSMainPage";
import {
  AddPrintKeys,
  PrintViewTypes,
} from "../../../label_print/assets/VarConstants";
import { useTranslation } from "react-i18next";
import { Save } from "@mui/icons-material";

const PrintingVM = ({
  material,
  bin_location,
  storage_loc,
  popupType = "material",
  setBinLocation,
  setMaterialDetails
}) => {
  const { handleToastOpen } = useToast();
  const [loading, setLoading] = useState(false);
  const [poOptions, setPoOptions] = useState([]);
  const [sizeOptions, setSizeOptions] = useState([]);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const { t } = useTranslation();

  // Default values
  const defaultStickerData = {
    [AddPrintKeys.PO_KEY]: "",
    [AddPrintKeys.MAT_NO_KEY]: material || "",
    [AddPrintKeys.SIZE_KEY]: "",
    [AddPrintKeys.QUANTITY_KEY]: 1,
    [AddPrintKeys.BIN_LOCATION_KEY]: bin_location || "",
    [AddPrintKeys.STORAGE_LOC_KEY]: storage_loc || "",
    [AddPrintKeys.BULK_LOCATION_KEY]: "",
  };

  const [stickerData, setStickerData] = useState(defaultStickerData);
  const [selectedSize, setSelectedSize] = useState();
  const buttonJson = {
    view: {
      text: "View",
      tooltipText: t('view_pdf'),
      isDisabled: false,
      src: <PictureAsPdfIcon />,
    },
    print_all: {
      text: "Print All",
      tooltipText: t('email_pdf'),
      isDisabled: false,
      src: <MailIcon />,
    },
    save: {
      text: "Save",
      tooltipText: t('Save bin bulk location'),
      isDisabled: false,
      src: <Save />,
    },
  };
  const [openCarousel, setOpenCarousel] = useState(false);
  const [bulkLocation, setBulkLocation] = useState('');

  // Reset to default values function
  const resetToDefaults = () => {
    const resetData = {
      [AddPrintKeys.PO_KEY]: "",
      [AddPrintKeys.MAT_NO_KEY]: material || "",
      [AddPrintKeys.SIZE_KEY]: "",
      [AddPrintKeys.QUANTITY_KEY]: 1,
      [AddPrintKeys.BIN_LOCATION_KEY]: bin_location || "",
      [AddPrintKeys.STORAGE_LOC_KEY]: storage_loc || "",
      [AddPrintKeys.BULK_LOCATION_KEY]: bulkLocation || "",
    };
    setStickerData(resetData);
    setSelectedSize(null);

    // Auto-select if only one size option available (excluding warehouse items)
    const availableSizes = sizeOptions.filter(
      (item) => item.is_warehouse !== 1
    );
    if (availableSizes.length === 1) {
      setSelectedSize(availableSizes[0]);
      setStickerData(prev => ({
        ...prev,
        [AddPrintKeys.SIZE_KEY]: availableSizes[0].id,
      }));
    }
  };

  // Fixed fetchBulkLocation function
  const fetchBulkLocation = async (materialNumber) => {
    try {
      if (materialNumber && materialNumber.trim() !== "") {
        const response = await getBulkLocationQuery(materialNumber);
        console.log(response.data[0]?.bulk_location);

        const fetchedBulkLocation = response.data[0]?.bulk_location || '';
        setBulkLocation(fetchedBulkLocation);

        // Update stickerData with fetched bulk location
        setStickerData((prev) => ({
          ...prev,
          [AddPrintKeys.BULK_LOCATION_KEY]: fetchedBulkLocation,
        }));
      } else {
        setBulkLocation('');
        setStickerData((prev) => ({
          ...prev,
          [AddPrintKeys.BULK_LOCATION_KEY]: '',
        }));
      }
    } catch (error) {
      console.error("Error fetching bulk location:", error);
      setBulkLocation('');
      setStickerData((prev) => ({
        ...prev,
        [AddPrintKeys.BULK_LOCATION_KEY]: '',
      }));
    }
  };

  // Fetch PO options based on material number
  const fetchPoOptions = async (materialNumber) => {
    try {
      if (materialNumber && materialNumber !== "") {
        const response = await fetchPoOrMaterial("", materialNumber);
        setPoOptions(response.data);
      } else {
        setPoOptions([]);
      }
    } catch (error) {
      setPoOptions([]);
    }
  };

  useEffect(() => {
    const fetchSize = async () => {
      try {
        const response = await fetchSizeOptions();
        setSizeOptions(response.data);

        // Auto-select if only one size option available (excluding warehouse items)
        const availableSizes = response.data.filter(
          (item) => item.is_warehouse !== 1
        );
        if (availableSizes.length === 1) {
          setSelectedSize(availableSizes[0]);
          setStickerData((prev) => ({
            ...prev,
            [AddPrintKeys.SIZE_KEY]: availableSizes[0].id,
          }));
        }
      } catch (error) {
        handleToastOpen("error", error.message);
        setSizeOptions([]);
      }
    };

    fetchSize();
  }, []);

  // Fetch PO options and bulk location when material is available
  useEffect(() => {
    if (material) {
      if (popupType === "material") {
        fetchPoOptions(material);
      }

      // Fetch bulk location for the material
      fetchBulkLocation(material);

      // Update stickerData with material number and bin_location
      setStickerData((prev) => ({
        ...prev,
        [AddPrintKeys.MAT_NO_KEY]: material,
        [AddPrintKeys.BIN_LOCATION_KEY]: bin_location || "",
        [AddPrintKeys.STORAGE_LOC_KEY]: storage_loc || "",
      }));
    }
  }, [material, bin_location, storage_loc, popupType]);

  const handleStickerDataEdit = (key) => (value) => {
    setStickerData((prevStickerData) => ({
      ...prevStickerData,
      [key]: typeof value === "string" && value !== null ? value.trim() : value,
    }));
  };

  const handleSizeChange = (value) => {
    setSelectedSize(value);
    if (value === null || value === "") {
      handleStickerDataEdit(AddPrintKeys.SIZE_KEY)("");
    } else {
      handleStickerDataEdit(AddPrintKeys.SIZE_KEY)(value.id);
    }
  };

  const validateStickers = () => {
    if (popupType === "material") {
      if (stickerData[AddPrintKeys.QUANTITY_KEY] === "") {
        handleToastOpen("error", "Please enter the quantity");
        return false;
      }
      if (stickerData[AddPrintKeys.MAT_NO_KEY] === "") {
        handleToastOpen("error", "Please enter the material number");
        return false;
      }
      if (stickerData[AddPrintKeys.SIZE_KEY] === "") {
        handleToastOpen("error", "Please enter the size");
        return false;
      }
    } else if (popupType === "warehouse") {
      if (stickerData[AddPrintKeys.SIZE_KEY] === "") {
        handleToastOpen("error", "Please enter the size");
        return false;
      }
      if (stickerData[AddPrintKeys.BIN_LOCATION_KEY] === "") {
        handleToastOpen("error", "Please enter the bin location");
        return false;
      }
    }
    return true;
  };

  // Material popup PDF view/print
  const materialPdfPrintView = async (id, stickerData, isPrint, type, closeMainPopup) => {
    try {
      if (!stickerData.print_qty) {
        handleToastOpen("error", "Please enter the quantity.");
        return false;
      }
      if (stickerData.print_qty > 500) {
        handleToastOpen("error", "Quantity should be in between 1-500.");
        return false;
      }
      if (stickerData.bin_location.length !== 0) {

        if (
          stickerData.bin_location.includes(" ")
        ) {
          handleToastOpen(
            'error',
            'Bin location cannot contain spaces.'
          );
          return false;
        }

        if (
          stickerData.bin_location.length === 0 ||
          stickerData.bin_location.length > 10
        ) {
          handleToastOpen(
            "error",
            "Bin location must be 10 characters or less."
          );
          return false;
        }
      }
      if (stickerData.bulk_location.length !== 0) {
        // if (
        //   stickerData.bulk_location.length === 0 ||
        //   stickerData.bulk_location.length > 10
        // ) {
        //   handleToastOpen(
        //     "error",
        //     "Bulk location must be 10 characters or less."
        //   );
        //   return false;
        // }
      }
      if (!stickerData.measurement_id) {
        handleToastOpen("error", "Please enter the size.");
        return false;
      }
      setLoading(true);
      const response = await getStickerPdf(id, stickerData, type, isPrint);
      if (isPrint) {
        if (response) {
          handleToastOpen("success", "Material Label email sent successfully.");

          const message = t("label_email");
          setSuccessMessage(message);
          setShowSuccessPopup(true);
          if (closeMainPopup) {
            closeMainPopup();
          }
        }
      } else {
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
    } catch (error) {
      handleToastOpen("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  // Warehouse popup PDF view/print
  const warehousePdfPrintView = async (id, stickerData, isPrint, type, closeMainPopup) => {
    try {
      if (!stickerData.bin_location) {
        handleToastOpen("error", "Please enter the bin location.");
        return false;
      }
      if (
        stickerData.bin_location.length === 0 ||
        stickerData.bin_location.length > 10
      ) {
        handleToastOpen("error", "Bin location must be 10 characters or less.");
        return false;
      }
      if (!stickerData.measurement_id) {
        handleToastOpen("error", "Please enter the size.");
        return false;
      }
      setLoading(true);

      const response = await getWarehousePdf(id, stickerData, type, isPrint);

      if (isPrint) {
        if (response) {
          handleToastOpen("success", "Warehouse Label email sent successfully.");

          const message = t("warehouse_email");
          setSuccessMessage(message);
          setShowSuccessPopup(true);
          if (closeMainPopup) {
            closeMainPopup();
          }
        }
      } else {
        const blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        const url = window.URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
    } catch (error) {
      handleToastOpen("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = async (key, closeMainPopup) => {
    console.log("in handel button click");
    if (key !== "save") {
      console.log("inside if");
      const isValidated = validateStickers();
      if (!isValidated) return;
    }

    if (key === "view") {
      if (popupType === "material") {
        materialPdfPrintView(
          undefined,
          stickerData,
          false,
          PrintViewTypes.PRINT_VIEW_DATA
        );
      } else if (popupType === "warehouse") {
        warehousePdfPrintView(
          undefined,
          stickerData,
          false,
          PrintViewTypes.PRINT_VIEW_DATA
        );
      }
    } else if (key === "print_all") {
      if (popupType === "material") {
        materialPdfPrintView(
          undefined,
          stickerData,
          true,
          PrintViewTypes.PRINT_VIEW_ALL,
          closeMainPopup
        );
      } else if (popupType === "warehouse") {
        warehousePdfPrintView(
          undefined,
          stickerData,
          true,
          PrintViewTypes.PRINT_VIEW_ALL,
          closeMainPopup
        );
      }
    }
    else if (key === "save") {
      console.log(stickerData[AddPrintKeys.BULK_LOCATION_KEY]);
      console.log("bulk location\n", stickerData[AddPrintKeys.BULK_LOCATION_KEY]);
      console.log("bin location\n", stickerData[AddPrintKeys.BIN_LOCATION_KEY]);
      console.log("material_no", material);
      console.log("logging storage loc", storage_loc);
      let response;
      try {
        response = await updateBinBulk(material, stickerData[AddPrintKeys.BIN_LOCATION_KEY], stickerData[AddPrintKeys.BULK_LOCATION_KEY], storage_loc)
        if (response.data === "Ok") {
          handleToastOpen("success", "Location Updated")
          setBinLocation(stickerData[AddPrintKeys.BIN_LOCATION_KEY]);
          setMaterialDetails(prevDetails => {
            if (Array.isArray(prevDetails) && prevDetails.length > 0) {
              const updatedDetails = [...prevDetails];
              updatedDetails[0] = {
                ...updatedDetails[0],
                bin_location: stickerData[AddPrintKeys.BIN_LOCATION_KEY],
                bulk_location: stickerData[AddPrintKeys.BULK_LOCATION_KEY],
              };
              return updatedDetails;
            }
            return prevDetails;
          });
          setStickerData((prev) => ({
            ...prev,
            [AddPrintKeys.BULK_LOCATION_KEY]: stickerData[AddPrintKeys.BULK_LOCATION_KEY],
            [AddPrintKeys.BIN_LOCATION_KEY]: stickerData[AddPrintKeys.BIN_LOCATION_KEY],
          }));
        }
      }
      catch (error) {
        handleToastOpen("error", "Something unexpected happened")
      }

    }
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    setSuccessMessage("");
  };

  return {
    poOptions,
    sizeOptions,
    selectedSize,
    stickerData,
    handleStickerDataEdit,
    buttonJson,
    bulkLocation,
    handleSizeChange,
    handleButtonClick,
    openCarousel,
    setOpenCarousel,
    loading,
    resetToDefaults,
    showSuccessPopup,
    successMessage,
    handleCloseSuccessPopup,
  };
};

export default PrintingVM;